/**
 * Text helpers of the attention block rows (KAN-628, KAN-734, KAN-774, KAN-783, KAN-790, KAN-794).
 */
import type { AttentionItem, AttentionReason, AttentionTask } from '@/services/api/dashboard'
import { DEFAULT_ORG_TIMEZONE, formatZonedShort } from '@/lib/orgTimezone'
import type { NextContactTarget } from '@/lib/nextContact'

/** Day, month, hours and minutes; `timeZone` fixes the zone (default: the browser's). */
export function formatAttentionDate(dateString: string, timeZone?: string): string {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  })
}

/**
 * KAN-790: due of a manager task, "до 12.10 14:00"; a broken date gives ''.
 * KAN-840: in the organization time zone (default Moscow).
 */
export function formatTaskDue(dueAt: string, timeZone: string = DEFAULT_ORG_TIMEZONE): string {
  const text = formatZonedShort(dueAt, timeZone)
  return text ? `до ${text}` : ''
}

export function attentionItemTitle(item: AttentionItem): string {
  return item.name || item.company || 'Клиент'
}

/** KAN-960: клиент строки для модалки "Следующий контакт". */
export function attentionNextContactTarget(item: AttentionItem): NextContactTarget {
  return {
    clientId: item.client_id,
    name: attentionItemTitle(item),
    manager: item.manager ?? null,
    followupAt: item.followup_at ?? null,
    followupType: item.followup_type || null,
    followupLabel: item.followup_label || null,
  }
}

/** KAN-774: reasons of an exhausted nudge (the "Предлагали" chips, a call); KAN-919 product_declined. */
const EXHAUST_REASONS: ReadonlySet<AttentionReason> = new Set<AttentionReason>([
  'nudge_exhausted',
  'replied_silent',
  'product_declined',
])

/** KAN-790: reasons with the "Предлагали" chips: the exhaust reasons and a manager task. */
const OFFERED_REASONS: ReadonlySet<AttentionReason> = new Set<AttentionReason>([
  ...EXHAUST_REASONS,
  'manager_task',
])

/**
 * Reasons whose next step is a call from a human: the exhaust reasons, KAN-790
 * a manager task, KAN-783 a lead attribute the owner flagged as attention-worthy and
 * KAN-890/KAN-907 an open lead with a manager or taken to work left without activity (idle).
 */
const CALL_REASONS: ReadonlySet<AttentionReason> = new Set<AttentionReason>([
  ...OFFERED_REASONS,
  'lead_attribute',
  'idle',
])

/** The "Предлагали: ..." tail of reason_text (shown as chips instead). */
const OFFERED_TAIL = /\s*Предлагали:.*$/su

/**
 * KAN-774: reason_text without the "Предлагали: ..." tail. The backend puts
 * the offered list into the text; the row shows it as chips, so the text is
 * cut here to avoid showing it twice. Without the marker the text is returned
 * as is.
 */
export function splitOfferedText(reasonText: string): string {
  return reasonText.replace(OFFERED_TAIL, '').trim()
}

/** KAN-774: the row has nudge_exhausted or replied_silent among its reasons. */
export function hasExhaustReason(item: AttentionItem): boolean {
  return item.reasons.some((reason) => EXHAUST_REASONS.has(reason))
}

/** KAN-790: the row shows the "Предлагали" chips (exhaust reasons or manager_task). */
export function hasOfferedReason(item: AttentionItem): boolean {
  return item.reasons.some((reason) => OFFERED_REASONS.has(reason))
}

/**
 * KAN-790: the manager task of a row (an attention item or a CRM board row),
 * or null (no task, an empty text, an older backend without the field).
 */
export function attentionTask(row: { task?: AttentionTask | null }): AttentionTask | null {
  return row.task?.text?.trim() ? row.task : null
}

/** KAN-790: the task line of a row, "Задача: <text>, до 12.10 14:00" (no due: without the tail). */
export function formatTaskLine(task: AttentionTask, timeZone: string = DEFAULT_ORG_TIMEZONE): string {
  const due = formatTaskDue(task.due_at, timeZone)
  return `Задача: ${task.text.trim()}${due ? `, ${due}` : ''}`
}

/**
 * Reason text of the row without the "Предлагали" tail when the chips are
 * shown (KAN-774). KAN-790: for a manager task the backend sends a short title
 * here and the task text in `task.text`, so the text is not compared with the task.
 */
export function attentionReasonText(item: AttentionItem): string {
  const chips = hasOfferedReason(item) && (item.offered ?? []).length > 0
  return chips ? splitOfferedText(item.reason_text) : item.reason_text
}

/** The row has a reason whose next step is a call (exhaust, manager_task, lead_attribute or idle). */
export function hasCallReason(item: AttentionItem): boolean {
  return item.reasons.some((reason) => CALL_REASONS.has(reason))
}

/** KAN-774, KAN-783: the row needs the "Позвонить" link (a phone and a call reason). */
export function needsCall(item: AttentionItem): boolean {
  return !!item.phone?.trim() && hasCallReason(item)
}

/** KAN-774: tel: href of a phone (spaces, brackets and dashes removed). */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[\s()-]/g, '')}`
}

/**
 * KAN-794: the line under the block header about open clients without a next
 * step (no planned touch, no attention row, no outcome). Null when there is
 * nothing to show: zero, no field (an older backend), no data yet.
 */
export function noNextStepLine(
  list: { no_next_step?: number | null } | null | undefined
): string | null {
  const count = list?.no_next_step
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return null
  return `Без следующего шага: ${count}. Появятся здесь после ежедневной проверки`
}
